import { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Users, ShoppingBag, Recycle, TrendUp, Leaf, Globe, HandHeart } from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#2C3E2D', '#4A6C4C', '#C26D53', '#D4A373', '#F5F2EB', '#E8E4DA'];

const ImpactDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/impact/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const categoryData = stats?.products_by_category 
    ? Object.entries(stats.products_by_category).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 pb-12 bg-[#2C3E2D] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Badge variant="secondary" className="mb-4 bg-white/10 border-white/20">
            <Globe weight="duotone" className="w-4 h-4 mr-2" />
            Platform Impact
          </Badge>
          <h1 className="text-4xl md:text-5xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Our Collective Impact
          </h1>
          <p className="text-lg text-white/70 max-w-2xl">
            Every purchase on GinsGood contributes to artisan empowerment, 
            cultural preservation, and environmental sustainability. See the difference we're making together.
          </p>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="py-12 -mt-6" data-testid="impact-dashboard">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                <StatCard 
                  icon={<Users weight="duotone" />}
                  value={stats?.total_artisans || 0}
                  label="Artisans Empowered"
                  suffix="+"
                  color="#4A6C4C"
                />
                <StatCard 
                  icon={<ShoppingBag weight="duotone" />}
                  value={stats?.total_products || 0}
                  label="Products Listed"
                  suffix="+"
                  color="#2C3E2D"
                />
                <StatCard 
                  icon={<Recycle weight="duotone" />}
                  value={stats?.carbon_saved_kg || 0}
                  label="CO₂ Saved"
                  suffix=" kg"
                  color="#C26D53"
                />
                <StatCard 
                  icon={<TrendUp weight="duotone" />}
                  value={stats?.average_transparency_score || 0}
                  label="Avg. Impact Score"
                  suffix=""
                  color="#D4A373"
                />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
                {/* Categories Pie Chart */}
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Products by Category
                  </h3>
                  {categoryData.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={categoryData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {categoryData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No category data available</p>
                  )}
                </Card>

                {/* Fair Trade Impact */}
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Fair Trade Impact
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Artisan Revenue Share</span>
                        <span className="font-semibold text-[#4A6C4C]">85%</span>
                      </div>
                      <div className="h-4 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-[#4A6C4C] rounded-full" style={{ width: '85%' }} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-secondary/50 rounded-lg p-4 text-center">
                        <HandHeart weight="duotone" className="w-8 h-8 mx-auto text-[#4A6C4C] mb-2" />
                        <p className="text-2xl font-bold text-[#4A6C4C]">${stats?.total_artisan_earnings?.toFixed(0) || 0}</p>
                        <p className="text-sm text-muted-foreground">Total Artisan Earnings</p>
                      </div>
                      <div className="bg-secondary/50 rounded-lg p-4 text-center">
                        <ShoppingBag weight="duotone" className="w-8 h-8 mx-auto text-[#2C3E2D] mb-2" />
                        <p className="text-2xl font-bold text-[#2C3E2D]">{stats?.total_orders || 0}</p>
                        <p className="text-sm text-muted-foreground">Orders Completed</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Impact Highlights */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <ImpactHighlight 
                  icon={<Leaf weight="duotone" className="w-8 h-8" />}
                  title="Sustainable Materials"
                  description="All products use verified sustainable materials with full traceability"
                />
                <ImpactHighlight 
                  icon={<Globe weight="duotone" className="w-8 h-8" />}
                  title="Cultural Preservation"
                  description="Supporting traditional crafts and techniques passed down through generations"
                />
                <ImpactHighlight 
                  icon={<Users weight="duotone" className="w-8 h-8" />}
                  title="Community Empowerment"
                  description="Direct connection between artisans and conscious consumers worldwide"
                />
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const StatCard = ({ icon, value, label, suffix, color }) => (
  <Card className="p-6 bg-white shadow-md">
    <div className="flex items-center gap-4">
      <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <span className="w-7 h-7" style={{ color }}>{icon}</span>
      </div>
      <div>
        <p className="text-3xl font-bold" style={{ color }}>
          {typeof value === 'number' ? value.toFixed(value % 1 !== 0 ? 1 : 0) : value}{suffix}
        </p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  </Card>
);

const ImpactHighlight = ({ icon, title, description }) => (
  <Card className="p-6 text-center">
    <div className="w-16 h-16 mx-auto bg-[#4A6C4C]/10 rounded-full flex items-center justify-center text-[#4A6C4C] mb-4">
      {icon}
    </div>
    <h3 className="font-medium mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground">{description}</p>
  </Card>
);

export default ImpactDashboard;
